import React, { useState, useMemo } from 'react';
import { Tab, Nav, Card, Badge, Form, Row, Col, Button, InputGroup, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const OrdersTabbed = ({ data, onRefresh }) => {
  const [activeOrderTab, setActiveOrderTab] = useState('sales');
  const [filters, setFilters] = useState({
    status: '',
    dateRange: '',
    search: ''
  });

  const { salesOrders, purchaseOrders, productionRequests } = data;

  // Filter and search logic
  const filteredOrders = useMemo(() => {
    let orders = [];

    switch (activeOrderTab) {
      case 'sales':
        orders = salesOrders;
        break;
      case 'purchase':
        orders = purchaseOrders;
        break;
      case 'production':
        orders = productionRequests;
        break;
      default:
        orders = [];
    }

    // Apply filters
    if (filters.status) {
      orders = orders.filter(order => order.status === filters.status);
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      orders = orders.filter(order =>
        (order.orderNumber && order.orderNumber.toLowerCase().includes(searchTerm)) ||
        (order.requestNumber && order.requestNumber.toLowerCase().includes(searchTerm)) ||
        (order.buyer?.business_name && order.buyer.business_name.toLowerCase().includes(searchTerm)) ||
        (order.vendorId?.business_name && order.vendorId.business_name.toLowerCase().includes(searchTerm))
      );
    }

    return orders;
  }, [activeOrderTab, salesOrders, purchaseOrders, productionRequests, filters]);

  // Get unique statuses for filter dropdown
  const getUniqueStatuses = () => {
    let orders = [];
    switch (activeOrderTab) {
      case 'sales':
        orders = salesOrders;
        break;
      case 'purchase':
        orders = purchaseOrders;
        break;
      case 'production':
        orders = productionRequests;
        break;
      default:
        orders = salesOrders;
        break;
    }
    return [...new Set(orders.map(order => order.status))];
  };

  // Status counts for tabs
  const getStatusCounts = (orders) => {
    const counts = {};
    orders.forEach(order => {
      const status = order.status || 'Unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: '', dateRange: '', search: '' });
  };

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Orders Tracking</h5>
          <Button variant="outline-secondary" size="sm" onClick={onRefresh}>
            <i className="fas fa-sync-alt me-1"></i>Refresh
          </Button>
        </div>
      </Card.Header>

      <Card.Body>
        <Tab.Container activeKey={activeOrderTab} onSelect={setActiveOrderTab}>
          <Nav variant="pills" className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="sales">
                Sales Orders
                <Badge bg="primary" className="ms-2">{salesOrders.length}</Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="purchase">
                Purchase Orders
                <Badge bg="success" className="ms-2">{purchaseOrders.length}</Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="production">
                Production Requests
                <Badge bg="warning" className="ms-2">{productionRequests.length}</Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Filters */}
          <Row className="mb-4">
            <Col md={3}>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                {getUniqueStatuses().map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search orders..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
                <Button variant="outline-secondary" onClick={clearFilters}>
                  <i className="fas fa-times"></i>
                </Button>
              </InputGroup>
            </Col>
            <Col md={5}>
              <div className="d-flex gap-2 flex-wrap">
                {Object.entries(getStatusCounts(filteredOrders)).map(([status, count]) => (
                  <Badge
                    key={status}
                    bg={getStatusColor(status)}
                    className="d-flex align-items-center gap-1"
                  >
                    {status}: {count}
                  </Badge>
                ))}
              </div>
            </Col>
          </Row>

          <Tab.Content>
            <Tab.Pane eventKey="sales">
              <OrdersTable orders={filteredOrders} type="sales" />
            </Tab.Pane>
            <Tab.Pane eventKey="purchase">
              <OrdersTable orders={filteredOrders} type="purchase" />
            </Tab.Pane>
            <Tab.Pane eventKey="production">
              <OrdersTable orders={filteredOrders} type="production" />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Card.Body>
    </Card>
  );
};

const OrdersTable = ({ orders, type }) => {
  if (!orders || orders.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No orders found</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table hover className="mb-0">
        <thead className="table-light">
          <tr>
            <th>Order/Request #</th>
            <th>Date</th>
            {type === 'sales' && <th>Buyer</th>}
            {type === 'purchase' && <th>Vendor</th>}
            {type === 'production' && <th>Product</th>}
            <th>Status</th>
            {type !== 'production' && <th>Expected Date</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, index) => (
            <tr key={order._id}>
              <td>
                <Link
                  to={`/${type === 'sales' ? 'sales' : type === 'purchase' ? 'purchase' : 'production'}-orders/${order._id}`}
                  className="fw-medium text-decoration-none"
                >
                  {order.orderNumber || order.requestNumber || `#${order._id.slice(-6)}`}
                </Link>
              </td>
              <td>
                {new Date(order.orderDate || order.createdAt).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </td>
              {type === 'sales' && (
                <td>{order.buyer?.business_name || '-'}</td>
              )}
              {type === 'purchase' && (
                <td>{order.vendorId?.business_name || '-'}</td>
              )}
              {type === 'production' && (
                <td>{order.productId?.productName || '-'}</td>
              )}
              <td>
                <Badge bg={getStatusColor(order.status)}>
                  {order.status}
                </Badge>
              </td>
              {type !== 'production' && (
                <td>
                  {order.EDD ? new Date(order.EDD).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  }) : '-'}
                </td>
              )}
              <td>
                <div className="d-flex gap-1">
                  <Link
                    to={`/${type === 'sales' ? 'sales' : type === 'purchase' ? 'purchase' : 'production'}-orders/${order._id}`}
                    className="btn btn-sm btn-outline-primary"
                  >
                    View
                  </Link>
                  {type === 'sales' && order.status === 'Confirmed' && (
                    <Link
                      to={`/sales-orders/${order._id}/dispatch`}
                      className="btn btn-sm btn-outline-success"
                    >
                      Dispatch
                    </Link>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const getStatusColor = (status) => {
  const statusColors = {
    'Pending': 'warning',
    'Created': 'info',
    'Confirmed': 'primary',
    'In Progress': 'primary',
    'Partially Delivered': 'secondary',
    'Delivered': 'success',
    'Completed': 'success',
    'Closed': 'success',
    'Cancelled': 'danger',
    'Rejected': 'danger',
    'Loaded': 'secondary',
    'Dispatched': 'info'
  };
  return statusColors[status] || 'light';
};

export default OrdersTabbed;